"use client";

import { useEffect, useRef, useState } from "react";
import { CalendarIcon, ChevronDownIcon } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export type DateRange = { from: string | null; to: string | null };

function toISODate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function label(range: DateRange) {
  if (!range.from && !range.to) return "Fecha";
  const parse = (s: string) => format(new Date(`${s}T00:00`), "d MMM", { locale: es });
  if (range.from && range.to && range.from === range.to) return parse(range.from);
  if (range.from && range.to) return `${parse(range.from)} – ${parse(range.to)}`;
  if (range.from) return `Desde ${parse(range.from)}`;
  return `Hasta ${parse(range.to!)}`;
}

export function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = !!(value.from || value.to);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  function quick(action: "today" | "clear") {
    if (action === "clear") return onChange({ from: null, to: null });
    const from = toISODate(new Date());
    onChange({ from, to: from });
  }

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        size="sm"
        variant={active ? "default" : "outline"}
        className="rounded-full"
        onClick={() => setOpen((o) => !o)}
      >
        <CalendarIcon className="size-3.5" />
        {label(value)}
        <ChevronDownIcon className="size-3.5" />
      </Button>

      {open && (
        <div className="absolute top-full left-0 z-20 mt-2 w-72 space-y-3 rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-md">
          <div className="flex flex-wrap gap-1.5">
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => quick("today")}>
              Hoy
            </Button>
            {active && (
              <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={() => quick("clear")}>
                Limpiar
              </Button>
            )}
          </div>

          <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Selecciona fecha
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="date-from" className="text-xs">
                Desde
              </Label>
              <Input
                id="date-from"
                type="date"
                value={value.from ?? ""}
                onChange={(e) => onChange({ ...value, from: e.target.value || null })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="date-to" className="text-xs">
                Hasta
              </Label>
              <Input
                id="date-to"
                type="date"
                value={value.to ?? ""}
                onChange={(e) => onChange({ ...value, to: e.target.value || null })}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Deja "Hasta" igual que "Desde" para un día concreto.
          </p>
        </div>
      )}
    </div>
  );
}
